import {
  BULB_MAX_ENERGY,
  CONSUMPTION_TIERS,
  GENERATOR_INTERVALS,
  PRODUCTION_SURPLUS_MAX,
  PRODUCTION_SURPLUS_MIN,
  PULSE_ENERGY,
  SCORE_PER_PULSE_DELIVERED,
} from '../config/balance.js';
import type { ColorId } from '../config/colors.js';
import {
  buildNetworkAnalysis,
  isNodeReachableFromGenerator,
} from './network.js';
import { canLaunchPulse, pickPulseLaunch, type RouteCache } from './routing.js';
import type { GameNode, GameState, SimulationEvent } from './types.js';

export function consumptionRateForTier(tierId: GameNode['consumptionTier']): number {
  const tier = CONSUMPTION_TIERS.find((t) => t.id === tierId);
  return tier?.rate ?? 0.75;
}

export function bulbMaxEnergy(state: GameState): number {
  return BULB_MAX_ENERGY * state.runModifiers.bulbCapacityMult;
}

export function tickBulbConsumption(state: GameState, dt: number): SimulationEvent[] {
  const events: SimulationEvent[] = [];
  for (const node of state.nodes) {
    if (node.kind !== 'bulb') continue;
    const rate = consumptionRateForTier(node.consumptionTier);
    node.energy = Math.max(0, (node.energy ?? bulbMaxEnergy(state)) - rate * dt);
    if (node.energy <= 0 && !state.gameOver) {
      state.gameOver = true;
      state.paused = true;
      events.push({ type: 'gameOver', day: state.day, score: state.score });
    }
  }
  return events;
}

export function deliverPulseToNode(
  state: GameState,
  pulseTargetId: string,
  color: ColorId,
  energy: number,
): SimulationEvent | null {
  const node = state.nodes.find((n) => n.id === pulseTargetId);
  if (!node || node.kind !== 'bulb') return null;

  if (node.color !== color) {
    return null;
  }

  const maxE = bulbMaxEnergy(state);
  node.energy = Math.min(maxE, (node.energy ?? 0) + energy);
  state.pulsesDelivered++;
  state.score += SCORE_PER_PULSE_DELIVERED;
  recordBulbInflow(state, node.id, energy);
  return { type: 'pulseDelivered', pulseId: '', nodeId: node.id, energy };
}

const INFLOW_WINDOW_SEC = 5;

export function recordBulbInflow(state: GameState, nodeId: string, energy: number): void {
  state.bulbInflowLog.push({ nodeId, t: state.simTime, energy });
  const cutoff = state.simTime - INFLOW_WINDOW_SEC;
  if (state.bulbInflowLog.length > 64) {
    state.bulbInflowLog = state.bulbInflowLog.filter((e) => e.t >= cutoff);
  }
}

/** Average energy received /s over the last few sim-seconds. */
export function bulbInflowPerSecond(state: GameState, nodeId: string): number {
  const cutoff = state.simTime - INFLOW_WINDOW_SEC;
  let sum = 0;
  for (const e of state.bulbInflowLog) {
    if (e.nodeId === nodeId && e.t >= cutoff) sum += e.energy;
  }
  const window = Math.min(INFLOW_WINDOW_SEC, Math.max(state.simTime, 0.5));
  return sum / window;
}

export function generatorInterval(node: GameNode): number {
  const size = node.generatorSize ?? 'small';
  return GENERATOR_INTERVALS[size];
}

/** Max production rate of a generator (energy units / second). */
export function generatorProductionPerSecond(node: GameNode): number {
  const interval = generatorInterval(node);
  return interval > 0 ? PULSE_ENERGY / interval : 0;
}

/** Effective production including run production bonus. */
export function effectiveGeneratorProductionPerSecond(
  state: GameState,
  node: GameNode,
): number {
  const bonus = Math.max(0, state.runModifiers?.generatorProductionMult ?? 0);
  return generatorProductionPerSecond(node) * (1 + bonus);
}

export interface NetworkEnergyStats {
  bulbCount: number;
  generatorCount: number;
  /** Sum of effective production of same-color gens on this cable network. */
  networkProduction: number;
  /** Sum of consumption of same-color bulbs reachable on this network. */
  networkConsumption: number;
}

/**
 * Aggregate prod/conso for the cable network reachable from `generator`
 * (same color only).
 */
export function networkEnergyStatsForGenerator(
  state: GameState,
  generator: GameNode,
): NetworkEnergyStats {
  if (generator.kind !== 'generator' || !generator.color) {
    return {
      bulbCount: 0,
      generatorCount: 0,
      networkProduction: 0,
      networkConsumption: 0,
    };
  }
  const analysis = buildNetworkAnalysis(state);
  const color = generator.color;
  let bulbCount = 0;
  let generatorCount = 0;
  let networkProduction = 0;
  let networkConsumption = 0;

  for (const node of state.nodes) {
    if (node.color !== color) continue;
    if (node.kind === 'generator') {
      const onNet =
        node.id === generator.id ||
        isNodeReachableFromGenerator(state, analysis, generator, node);
      if (!onNet) continue;
      generatorCount++;
      networkProduction += effectiveGeneratorProductionPerSecond(state, node);
    } else if (node.kind === 'bulb') {
      if (!isNodeReachableFromGenerator(state, analysis, generator, node)) continue;
      bulbCount++;
      networkConsumption += consumptionRateForTier(node.consumptionTier);
    }
  }

  return { bulbCount, generatorCount, networkProduction, networkConsumption };
}

/** Cables attached to a generator (either endpoint). */
export function countCablesOnGenerator(state: GameState, generatorId: string): number {
  let n = 0;
  for (const c of state.cables) {
    if (c.endpointA === generatorId || c.endpointB === generatorId) n++;
  }
  return n;
}

/**
 * Spawn interval at the generator base rate, shortened by run production bonuses.
 * Power is split across attached cables by round-robin in `pickPulseLaunch`.
 */
export function effectiveGeneratorInterval(state: GameState, node: GameNode): number {
  const base = generatorInterval(node);
  const bonus = Math.max(0, state.runModifiers?.generatorProductionMult ?? 0);
  return base / (1 + bonus);
}

export function maxProductionPerSecond(
  color: ColorId,
  nodes: GameNode[],
  generatorProductionMult = 0,
): number {
  const rateMult = 1 + Math.max(0, generatorProductionMult);
  let total = 0;
  for (const n of nodes) {
    if (n.kind !== 'generator' || n.color !== color) continue;
    const interval = generatorInterval(n);
    total += (PULSE_ENERGY / interval) * rateMult;
  }
  return total;
}

export function totalConsumptionPerSecond(color: ColorId, nodes: GameNode[]): number {
  let total = 0;
  for (const n of nodes) {
    if (n.kind !== 'bulb' || n.color !== color) continue;
    total += consumptionRateForTier(n.consumptionTier);
  }
  return total;
}

function productionMultOf(state?: GameState | null): number {
  return Math.max(0, state?.runModifiers?.generatorProductionMult ?? 0);
}

/** production / consumption (Infinity if no bulbs). */
export function productionSurplusRatio(
  color: ColorId,
  nodes: GameNode[],
  generatorProductionMult = 0,
): number {
  const consumption = totalConsumptionPerSecond(color, nodes);
  if (consumption <= 0) return Infinity;
  return maxProductionPerSecond(color, nodes, generatorProductionMult) / consumption;
}

export function meetsProductionSurplus(
  color: ColorId,
  nodes: GameNode[],
  generatorProductionMult = 0,
): boolean {
  const consumption = totalConsumptionPerSecond(color, nodes);
  if (consumption <= 0) return true;
  return (
    productionSurplusRatio(color, nodes, generatorProductionMult) >= PRODUCTION_SURPLUS_MIN
  );
}

/** True when this color is already at/above the +50% production cap. */
export function isProductionAtCap(
  color: ColorId,
  nodes: GameNode[],
  generatorProductionMult = 0,
): boolean {
  const consumption = totalConsumptionPerSecond(color, nodes);
  if (consumption <= 0) {
    // Gens with no demand count as capped (don't pile more gens).
    return maxProductionPerSecond(color, nodes, generatorProductionMult) > 0;
  }
  return (
    productionSurplusRatio(color, nodes, generatorProductionMult) >= PRODUCTION_SURPLUS_MAX
  );
}

/** After hypothetically adding a bulb, would we stay above the +30% floor? */
export function wouldSurplusHoldAfterBulb(
  color: ColorId,
  nodes: GameNode[],
  extraConsumptionRate: number,
  generatorProductionMult = 0,
): boolean {
  const consumption = totalConsumptionPerSecond(color, nodes) + extraConsumptionRate;
  const production = maxProductionPerSecond(color, nodes, generatorProductionMult);
  if (consumption <= 0) return true;
  return production >= consumption * PRODUCTION_SURPLUS_MIN;
}

/**
 * May we spawn a bulb of this color?
 * - Normally: keep ≥ +30% after spawn.
 * - When already over +50%: allow draining surplus down to break-even.
 */
export function canSpawnBulbForColor(
  color: ColorId,
  nodes: GameNode[],
  extraConsumptionRate: number,
  generatorProductionMult = 0,
): boolean {
  const production = maxProductionPerSecond(color, nodes, generatorProductionMult);
  if (production <= 0) return false;
  const consumptionAfter =
    totalConsumptionPerSecond(color, nodes) + extraConsumptionRate;
  if (consumptionAfter <= 0) return true;
  const ratioAfter = production / consumptionAfter;
  if (isProductionAtCap(color, nodes, generatorProductionMult)) {
    return ratioAfter >= 1;
  }
  return ratioAfter >= PRODUCTION_SURPLUS_MIN;
}

/**
 * May we spawn another generator of this color?
 * Blocked at/above +50%, except when the next bulb (at `nextBulbRate`) still
 * cannot fit — then we must top up gens first.
 *
 * Important: `nextBulbRate` must be the rate that would actually spawn today
 * (e.g. 1.25 at day 50). Using only the tier-0 0.75 rate deadlocks mid/late game:
 * headroom for a 0.75 bulb blocks gens, while trySpawnBulb asks for 1.25 and fails.
 */
export function canSpawnGeneratorForColor(
  color: ColorId,
  nodes: GameNode[],
  nextBulbRate: number = CONSUMPTION_TIERS[0]!.rate,
  generatorProductionMult = 0,
): boolean {
  if (!isProductionAtCap(color, nodes, generatorProductionMult)) return true;
  return !canSpawnBulbForColor(color, nodes, nextBulbRate, generatorProductionMult);
}

export function ensureSurplusForNewBulb(state: GameState, color: ColorId, rate: number): boolean {
  return canSpawnBulbForColor(color, state.nodes, rate, productionMultOf(state));
}

export function canGeneratorProduce(
  state: GameState,
  cache: RouteCache,
  generator: GameNode,
): boolean {
  if (generator.kind !== 'generator' || !generator.color) return false;
  return canLaunchPulse(state, cache, generator);
}

export function tickGenerators(
  state: GameState,
  cache: RouteCache,
  dt: number,
  spawnPulse: (
    generator: GameNode,
    target: GameNode,
    preferStartCableId?: string,
  ) => void,
): void {
  for (const node of state.nodes) {
    if (node.kind !== 'generator' || !node.color) continue;
    node.productionTimer = (node.productionTimer ?? 0) + dt;
    const interval = effectiveGeneratorInterval(state, node);
    while (node.productionTimer >= interval) {
      node.productionTimer -= interval;
      if (!canGeneratorProduce(state, cache, node)) continue;
      const launch = pickPulseLaunch(state, cache, node);
      if (!launch) continue;
      spawnPulse(node, launch.target, launch.startCableId);
    }
  }
}

export function countColorsNeedingGenerator(state: GameState, color: ColorId, extraRate: number): boolean {
  return !canSpawnBulbForColor(
    color,
    state.nodes,
    extraRate,
    productionMultOf(state),
  );
}

export function colorNeedsGenerator(
  color: ColorId,
  nodes: GameNode[],
  generatorProductionMult = 0,
): boolean {
  const consumption = totalConsumptionPerSecond(color, nodes);
  if (consumption <= 0) {
    return maxProductionPerSecond(color, nodes, generatorProductionMult) === 0;
  }
  return (
    productionSurplusRatio(color, nodes, generatorProductionMult) < PRODUCTION_SURPLUS_MIN
  );
}
